import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  LayoutDashboard,
  Users,
  Activity,
  Settings,
  LogOut,
  Menu,
  X,
  Moon,
  Sun,
  Bell,
  BarChart3,
  MessageCircle,
  Shield,
  Key,
  FileText,
  Megaphone,
  ChevronLeft,
  Search,
  TrendingUp,
  Zap,
  CalendarOff,
  Database,
  CalendarDays,
  Wallet,
  Receipt
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import NotificationPopup from '../components/NotificationPopup';

const DashboardLayout = () => {
  const { user, logout, isAdmin, isManager } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['executive', 'manager', 'admin'] },
    { path: '/today-sales', label: 'Today Sales', icon: Zap, roles: ['executive', 'manager', 'admin'] },
    { path: '/performance', label: 'My Performance', icon: TrendingUp, roles: ['executive', 'manager', 'admin'] },
    { path: '/leaves', label: 'Leave Management', icon: CalendarDays, roles: ['executive', 'manager', 'admin'] },
    { path: '/my-payslips', label: 'My Payslips', icon: Receipt, roles: ['executive', 'manager'] },
    { path: '/customers', label: 'Customer Hub', icon: Users, roles: ['executive', 'manager', 'admin'] },
    { path: '/chat', label: 'Chat', icon: MessageCircle, roles: ['executive', 'manager', 'admin'] },
    { path: '/team', label: 'Team Performance', icon: BarChart3, roles: ['manager', 'admin'] },
    { path: '/users', label: 'User Management', icon: Settings, roles: ['admin'] },
    { path: '/payroll', label: 'Payroll', icon: Wallet, roles: ['admin'] },
    { path: '/broadcasts', label: 'Broadcasts', icon: Megaphone, roles: ['admin'] },
    { path: '/holidays', label: 'Holiday Management', icon: CalendarOff, roles: ['admin'] },
    { path: '/leads', label: 'Leads', icon: Database, roles: ['admin', 'manager'] },
    { path: '/security', label: 'Security Settings', icon: Shield, roles: ['admin'] },
    { path: '/hr-documents', label: 'HR Documents', icon: FileText, roles: ['admin'] },
    { path: '/chat-monitor', label: 'Chat Monitor', icon: MessageCircle, roles: ['admin'] },
    { path: '/password-logs', label: 'Password Logs', icon: Key, roles: ['admin'] },
    { path: '/activity-logs', label: 'Activity Logs', icon: Activity, roles: ['admin'] },
    { path: '/change-password', label: 'Change Password', icon: Key, roles: ['executive', 'manager', 'admin'] },
  ];

  const filteredNavItems = navItems.filter(item => item.roles.includes(user?.role));

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const getPageTitle = () => {
    const path = location.pathname.split('/')[1];
    const titles = {
      dashboard: 'Dashboard',
      'today-sales': 'Today Sales',
      customers: 'Customer Hub',
      chat: 'Chat',
      team: 'Team Performance',
      users: 'User Management',
      broadcasts: 'Broadcasts',
      holidays: 'Holiday Management',
      security: 'Security Settings',
      'hr-documents': 'HR Documents',
      'chat-monitor': 'Chat Monitor',
      'password-logs': 'Password Logs',
      'activity-logs': 'Activity Logs',
      'change-password': 'Change Password',
      'performance': 'Performance Analytics',
      'leaves': 'Leave Management'
    };
    return titles[path] || 'Dashboard';
  };

  const getRoleBadgeClass = (role) => {
    const classes = {
      admin: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400',
      manager: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400',
      executive: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400'
    };
    return classes[role] || classes.executive;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col fixed left-0 top-0 h-full z-40 transition-all duration-200 ${
          sidebarOpen ? 'w-64' : 'w-[72px]'
        } bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            {sidebarOpen && (
              <span className="font-bold text-slate-900 dark:text-white tracking-tight">Omnilink</span>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="h-8 w-8 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto scrollbar-thin">
          {filteredNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.replace('/', '')}`}
              className={({ isActive }) =>
                `nav-item ${isActive ? 'active' : ''} ${!sidebarOpen ? 'justify-center px-2' : ''}`
              }
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="truncate">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User Section */}
        <div className="p-3 border-t border-slate-200 dark:border-slate-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={`w-full ${sidebarOpen ? 'justify-start' : 'justify-center'} gap-3 h-auto py-2.5 px-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg`}
                data-testid="user-menu-trigger"
              >
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-indigo-600 text-white text-xs font-semibold">
                    {getInitials(user?.first_name, user?.last_name)}
                  </AvatarFallback>
                </Avatar>
                {sidebarOpen && (
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${getRoleBadgeClass(user?.role)}`}>
                      {user?.role}
                    </span>
                  </div>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <DropdownMenuItem onClick={toggleTheme} className="text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="theme-toggle">
                {theme === 'light' ? <Moon className="w-4 h-4 mr-2" /> : <Sun className="w-4 h-4 mr-2" />}
                {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-200 dark:bg-slate-800" />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 z-50 flex items-center justify-between px-3 safe-area-inset">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="h-10 w-10 text-slate-600 dark:text-slate-400"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
          <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
          </div>
          <span className="font-bold text-slate-900 dark:text-white text-sm">Omnilink</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-10 w-10 text-slate-500" data-testid="mobile-notifications">
            <Bell className="w-5 h-5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={toggleTheme} className="h-10 w-10 text-slate-600 dark:text-slate-400">
            {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Sidebar */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
          <aside className="absolute left-0 top-14 bottom-0 w-72 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 animate-slide-in-right flex flex-col">
            {/* Scrollable nav area */}
            <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
              {filteredNavItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''} py-3 text-base`}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              ))}
            </nav>
            {/* User info + logout at bottom */}
            <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
              <div className="flex items-center gap-3 mb-3">
                <Avatar className="w-10 h-10">
                  <AvatarFallback className="bg-indigo-600 text-white text-sm font-semibold">
                    {getInitials(user?.first_name, user?.last_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${getRoleBadgeClass(user?.role)}`}>
                    {user?.role}
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                className="w-full justify-start gap-3 text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg py-3"
                onClick={handleLogout}
              >
                <LogOut className="w-5 h-5" />
                <span>Log out</span>
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-200 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-[72px]'} mt-14 lg:mt-0`}>
        {/* Top Bar */}
        <header className="hidden lg:flex h-14 items-center justify-between px-6 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-30">
          <h1 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight">
            {getPageTitle()}
          </h1>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-9 w-9 relative text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200" data-testid="notifications-btn">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-indigo-600 rounded-full" />
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-3 sm:p-4 md:p-6 lg:p-8 animate-fade-in">
          <Outlet />
        </div>
      </main>
      
      <NotificationPopup />
    </div>
  );
};

export default DashboardLayout;
